import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import PhotoPermissionsUtil from '../../../../../helpers/permissions/PhotoPermissionsUtil';
import BaseComponent from '../../../../../baseClasses/BaseComponent';
import fileTypeImageIcon from '../../../../../assets/images/photo-jpg-placeholder.svg';
import fileTypeNoImageRequired from '../../../../../assets/images/na-photo.svg';
import TaskCompletionStepPhotoGrid from './TaskCompletionStepPhotoGrid';
import StepApproval from '../StepApproval';
import TaskCompletionStepComments from '../TaskCompletionStepComments';
import CommentPopup from '../CommentPopup';
import AnalyticsLogger from '../../../../../helpers/analytics/analyticsLogger';
import dataModel from '../../../../../models/DataModel';
import FileUtil from '../../../../../helpers/FileUtil/FileUtil';
import PhotoUtil from '../../../../../helpers/PhotoUtil/PhotoUtil';
import ModalBannerConstants from '../../../../../constants/ModalBannerConstants';
import { completion_criteria_root } from '../../../../../dao/CompletionCriteriaDAO';
import DataSource from '../../../../../dao/DataSource/DataSource';

import './PhotoUpload.scss';
import MiscConstants from '../../../../../constants/MiscConstants';
import AttachmentUploading from '../../../../AttachmentUploading';

/**
 * This component is used in the tasks container for first-time photo upload to a task's completion step
 */
export class PhotoUpload extends BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      stepState: '',
      userCanModify: false,
      naButton: false,
    };
    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.acceptedFileTypes = FileUtil.getAcceptedExtensionsByType('jpg', false, true);
    this.showUploadModal = this.showUploadModal.bind(this);
  }

  handleCSUpdate(completionStep, action, acData) {
    this.setState({ completionStep });
    this.checkForNA();
  }

  componentDidMount() {
    const { completionStep } = this.props;

    this.csDataSource = new DataSource('CompletionCriterion', completionStep.id, completion_criteria_root, this.handleCSUpdate);
    this.csDataSource.get();
  }

  componentWillUnmount() {
    if (this.csDataSource) {
      this.csDataSource.release();
      this.csDataSource = null;
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.attachmentUploading !== this.state.attachmentUploading) {
      if(this.props.attachmentUploading == false){
        this.checkForNA();
      }
      this.setState({ attachmentUploading: this.props.attachmentUploading });
    }
  }

  checkForNA() {
    const { userStepPermissions } = this.props;
    const { completionStep } = this.state;
    if (!completionStep) {
      return;
    }
    if (!(completionStep.attachments && completionStep.attachments.length > 0) &&
      !completionStep.not_applicable && 
      userStepPermissions.has('step_complete') && 
      !completionStep.completed_at 
    ) {
      if (!this.state.naButton) this.setState({ naButton: true });
    } 
    else if (this.state.naButton) {
      this.setState({ naButton: false });
    }
  }

  handleFileSelect(fileToUpload) {
    const { completionStep } = this.state;
    // one more check to ensure they haven't yet reached upload limit...
    if (completionStep.attachments.length < MiscConstants.MAX_NUMBER_OF_PHOTOS) {
      let extension = FileUtil.getExtension(fileToUpload.name);
      this.setState({ uploadMethod: 'web' });
      PhotoUtil.handleResizeFile(fileToUpload, extension, null, 'web')
      .then(parsedFile => {
        const { file, resizedImage, exifData } = parsedFile;
        dataModel.attachmentUploading = true;
        return PhotoUtil.handleSaveFileOnWeb(this.props.task, this.props.completionStep, file, resizedImage, exifData);
      })
      .then(updatedCS => {
        updatedCS.completed_at = new Date().toJSON();
        this.setState({ completionStep: updatedCS });
      });
    }
  }

  showUploadModal() {
    const { completionStep } = this.state;
    dataModel.showGlobalModal(ModalBannerConstants.FILE_UPLOADER, {
      acceptedFileTypes: this.acceptedFileTypes,
      handleFileSelect: this.handleFileSelect,
      currentNumberOfFiles: completionStep.attachments.length,
      maximumAllowedFiles: MiscConstants.MAX_NUMBER_OF_PHOTOS,
    });
  }

  handleNoPhotoRequiredButton() {
    const { completionStep } = this.state;
    this.csDataSource.performAction('markNotApplicable', completionStep);
  }

  handleUndoNotApplicable() {
    const { completionStep } = this.state;
    this.csDataSource.performAction('unmarkNotApplicable', completionStep);
  }
  
  handleKeyDown(e, type) {
    if (e.keyCode == 27) {
      this.setState({ noPhotoRequiredComment: false, rejectionComment: false });
    } else if (e.keyCode == 13 || e.keyCode == 40) {
      e.preventDefault();
      this.handleCommentSent(type, this.state.commentText);
    }
  }

  handleCancelComment() {
    this.setState({
      noPhotoRequiredComment: false,
      rejectionComment: false,
      displayUploadButtons: false,
    });
    this.checkForNA();
  }

  handleCommentSent(type, commentText) {
    this.setState({ rejectionComment: false });
    
    const { completionStep } = this.state;
    this.csDataSource.performAction('rejectCompletionCriterion', completionStep, commentText);
  }

  handleCommentChange(e) {
    this.setState({ commentText: e.target.value });
  }

  handleStepApprove() {
    const { userStepPermissions } = this.props;
    const { completionStep } = this.state;
    if (userStepPermissions.has('is_approver')) {
      this.handleApproved(completionStep);
    }
    else if (userStepPermissions.has('is_reviewer')) {
      this.handleReviewed(completionStep);
    }
  }

  handleStepReject() {
    const { userStepPermissions, task } = this.props;
    if (userStepPermissions.has('accept/reject')) {
      if((task.has_reviewer && this.state.completionStep.reviewed_at) || !task.has_reviewer){
        this.handleRejectApproval(this.state.completionStep);
      }
      else{
        this.handleRejectReview(this.state.completionStep);
      }
    }
  }

  handlePhotoSelection(val) {
    this.setState({ photoSelected: val });
  }

  handleReviewed(completionStep) {
    this.csDataSource.performAction('reviewCompletionCriterion', completionStep);
  }

  handleRejectReview(completionStep) {
    this.setState({ rejectionComment: true });
  }

  handleApproved(completionStep) {
    this.csDataSource.performAction('approveCompletionCriterion', completionStep);
  }

  handleRejectApproval(completionStep) {
    this.setState({ rejectionComment: true });
  }

  setFocus(element) {
    if (element) {
      this.commentInput = element;
      element.focus();
    }
  }

  handleDetermineRejected(task){
    const stepsRejected = task.completion_criteria
    ? task.completion_criteria.filter(cc => {
      if (cc) {
        return (
          cc.rejected_at &&
          !cc.approved_at &&
          !cc.reviewed_at &&
          !cc.completed_at
        );
      }
      else {
        return false;
      }
    }
    )
    : [];
    return stepsRejected.length > 0;
  }

  render() {
    const { 
      editing, 
      task,
      userStepPermissions, 
    } = this.props;

    const { completionStep } = this.state;
    if (!completionStep) {
      return null;
    }
    let buttons = [];

    let reviewApproveButtons = null;
    if (!editing && !this.state.rejectionComment &&
      userStepPermissions.has('accept/reject') && !completionStep.rejected_at) {
      reviewApproveButtons = (
        <div className={'photo-approve-reject-buttons' + (completionStep.not_applicable ? ' na' : '')}>
          <StepApproval key={(task.has_reviewer && (userStepPermissions.has('is_reviewer') || this.props.currentUser.super_admin) &&
            !completionStep.reviewed_at ? '_review' : '_approve') + completionStep.id} userStepPermissions={userStepPermissions}
            handleStepReject={this.handleStepReject}
            handleStepApprove={this.handleStepApprove}
          />
        </div>
      );
    }

    let typeImage = null;
    let gridDisplayed = false;
    if (completionStep.not_applicable) {
      typeImage = (
        <img
          className="photo-default-image-display"
          src={fileTypeNoImageRequired}
          alt=""
        />
      );
    } else if (completionStep.attachments.length > 0) {
      gridDisplayed = true;
      typeImage = (
        <TaskCompletionStepPhotoGrid
          completionStep={completionStep}
          task={task}
          attachments={completionStep.attachments}
          status={this.props.status}
          withAcceptReject={reviewApproveButtons ? true : false}
          userStepPermissions={this.props.userStepPermissions}
        />
      );
    } else if (!editing) {
      typeImage = (
        <img
          className="photo-default-image-display"
          alt=""
          src={fileTypeImageIcon}
        />
      );
    }

    let buttonList;
    if (!editing) {
      if (this.state.naButton) {
        buttons.push(
          <div
            className="button na-button"
            key="no-image-button"
            onClick={this.handleNoPhotoRequiredButton}
          />
        );
      }
      if (
        !editing &&
        !completionStep.not_applicable &&
        PhotoPermissionsUtil.canUserEditPhoto(userStepPermissions, completionStep, task) &&
        (!completionStep.attachments || completionStep.attachments.length < MiscConstants.MAX_NUMBER_OF_PHOTOS)
      ) {
        buttons.push(
          <div
            className={'button add-button'}
            key="add-button"
            onClick={this.showUploadModal}
          >
          </div>
        );
      }
      if (
        completionStep.not_applicable && (userStepPermissions.has('unmarkNA') ||
        ((userStepPermissions.has('step_complete') || userStepPermissions.has('step_update')) &&  
        !completionStep.reviewed_at && !completionStep.approved_at &&
        (completionStep.status != 'completed'))
      )) {
        buttons.push(
          <div
            className="button undo-na-button"
            key="undo-na-button"
            onClick={this.handleUndoNotApplicable}
            data-qa="not-applicable-button"
          />
        );
      }

      buttonList = (
        <div
          className={`photo-button-list ${
            this.attachments && completionStep.attachments.length > 1 ? 'grid-expand ' : ''
          }
          ${
            completionStep.attachments.length == 1 ? 'single-photo' : ''
          }`
        }
        >
          {buttons}
        </div>
      );
    }

    let uploading = <AttachmentUploading/>;

    let displayComponent = typeImage;

    let commentDisplay = null;
    if (!editing) {
      if (this.state.noPhotoRequiredComment) {
        buttonList = null;
        const element = document.getElementById(
          'task-completion-step-comments-' + completionStep.id
        );
        const rect = element.getBoundingClientRect();
        commentDisplay = (
          <CommentPopup
            task={task}
            completionStep={completionStep}
            height={element.clientHeight}
            width={element.clientWidth}
            left={rect.left}
            top={rect.top - 20}
            type={'NO PHOTO REQUIRED'}
            commentSent={commentText =>
              this.handleCommentSent('na', commentText)
            }
            placeholder={
              'Leave a comment to mark as N/A.'
            }
            cancelComment={this.handleCancelComment}
          />
        );
      } else if (this.state.rejectionComment) {
        buttonList = null;
        const element = document.getElementById(
          'task-completion-step-comments-' + completionStep.id
        );
        const rect = element.getBoundingClientRect();
        commentDisplay = (
          <CommentPopup
            task={task}
            completionStep={completionStep}
            height={element.clientHeight}
            width={element.clientWidth}
            left={rect.left}
            top={rect.top - 20}
            type={'REJECTED'}
            commentSent={commentText =>
              this.handleCommentSent('reject', commentText)
            }
            placeholder={
              'You must leave a comment to reject a Completion Step.'
            }
            cancelComment={this.handleCancelComment}
          />
        );
      }
    }

    if (this.state.photoSelected) {
      buttonList = null;
    }

    let comments = null;
    if (!editing) {
      comments = (
        <TaskCompletionStepComments
          id={'task-completion-step-comments-' + completionStep.id}
          task={task}
          completionStep={completionStep}
        />
      );
    }

    if (this.props.projectTemplate){
      commentDisplay = null;
      comments = null;
      reviewApproveButtons = null;
      buttonList = null;
    }

    return (
      <div
        className="photo-upload-step"
        id={'photo-upload-step-' + completionStep.id}
      >
        <div className={'display-and-buttons ' + (gridDisplayed ? 'grid-displayed' : '')}>
          { completionStep.instructions && (
              <div className="photo-instructions">
                <div className="instruction-label">Instructions</div>
                <div className="instruction-text">
                  {completionStep.instructions}
                </div>
              </div>
            )}
          {!this.state.attachmentUploading ? displayComponent : uploading}
          {!this.state.attachmentUploading && buttonList}
        </div>
        {
          completionStep.attachments.length >= MiscConstants.MAX_NUMBER_OF_PHOTOS &&
          <span className="max-photos-text">Maximum number of photos reached. Delete an image if you wish to upload more. </span>
        }
        {commentDisplay}
        {reviewApproveButtons}
        {comments}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.new_users ? state.new_users.currentUser : null,
    attachmentUploading: state.main.attachmentUploading,
  };
};

export default connect(mapStateToProps)(PhotoUpload);



// WEBPACK FOOTER //
// ./src/components/TaskCRUD/TaskCompletionStepsContainer/TaskCompletionSteps/PhotoUpload/index.js